import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import MainLayout from '../../components/Layout/MainLayout';
import Button from '../../components/common/Button';
import CustomerCard from '../../components/Customers/CustomerCard';
import CustomerFunnel from '../../components/Customers/CustomerFunnel';
import { api } from '../../services/api';
import { Add, Search } from '@mui/icons-material';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  ${props => props.theme?.isDark && `
    text-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
  `}
`;

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const SearchInput = styled.input`
  padding: ${props => getTheme(props.theme?.isDark).spacing.sm} ${props => getTheme(props.theme?.isDark).spacing.md};
  padding-left: 40px;
  border: 1px solid ${props => getTheme(props.theme?.isDark).colors.border};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  background-color: ${props => getTheme(props.theme?.isDark).colors.surface};
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-size: 14px;
  min-width: 300px;
  transition: all 0.2s ease;
  font-family: ${props => getTheme(props.theme?.isDark).fonts.primary};

  &:focus {
    outline: none;
    border-color: ${props => getTheme(props.theme?.isDark).colors.primary};
    ${props => props.theme?.isDark && `
      box-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
    `}
  }

  &::placeholder {
    color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  pointer-events: none;
`;

const CustomersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${props => getTheme(props.theme?.isDark).spacing.lg};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xxl};
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xxl};
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  margin-top: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const CustomersList = () => {
  const { isDark } = useTheme();
  const theme = { isDark };
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [selectedFunnelId, setSelectedFunnelId] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, [page, search, selectedFunnelId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const filter = {};
      if (search) {
        filter.search = search;
      }
      if (selectedFunnelId) {
        filter.funnelId = selectedFunnelId;
      }
      const response = await api.getCustomers({
        page,
        limit,
        filter,
      });
      setCustomers(response.results || []);
      setTotal(response.totalResults || 0);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFunnelClick = (funnelId) => {
    setSelectedFunnelId(funnelId);
    setPage(1); // Reset to first page when filtering
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <MainLayout>
      <PageContainer theme={theme}>
        <PageHeader>
          <Title theme={theme}>Customers</Title>
          <SearchContainer>
            <SearchIcon theme={theme}>
              <Search />
            </SearchIcon>
            <SearchInput
              type="text"
              placeholder="Search by name, email, or phone..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              theme={theme}
            />
            <Button onClick={() => navigate('/admin/customers/new')} theme={theme}>
              <Add /> Create New
            </Button>
          </SearchContainer>
        </PageHeader>

        <CustomerFunnel onFunnelClick={handleFunnelClick} />

        {loading ? (
          <LoadingContainer theme={theme}>Loading customers...</LoadingContainer>
        ) : customers.length === 0 ? (
          <EmptyState theme={theme}>
            <p>No customers found</p>
            {(search || selectedFunnelId) && (
              <p style={{ marginTop: '8px', fontSize: '14px' }}>
                {search && 'Try adjusting your search terms'}
                {selectedFunnelId && !search && 'No customers match the selected funnel criteria'}
              </p>
            )}
          </EmptyState>
        ) : (
          <>
            <CustomersGrid>
              {customers.map((customer) => (
                <CustomerCard key={customer.id} customer={customer} />
              ))}
            </CustomersGrid>
            {totalPages > 1 && (
              <Pagination theme={theme}>
                <Button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  theme={theme}
                >
                  Previous
                </Button>
                <span style={{ color: getTheme(isDark).colors.textSecondary }}>
                  Page {page} of {totalPages}
                </span>
                <Button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  theme={theme}
                >
                  Next
                </Button>
              </Pagination>
            )}
          </>
        )}
      </PageContainer>
    </MainLayout>
  );
};

export default CustomersList;

